// Data Initialization and Transformation Utilities
// Functions for creating, initializing, and transforming SOW data structures.
// These are pure functions that create new objects rather than mutating.

import { PropertyType } from './sow.types';
import type {
  SOWData,
  CategoryData,
  FieldValues,
  ContingencyData,
  CurrencyValue,
} from './sow.types';
import { sowConfig, getCategoryById, getCategoriesForPropertyType } from './sow.config';
import { calculateContingency, recalculateTotals } from './calculations';

// Create empty field values for a category
// All fields initialized to 0
function createEmptyFieldValues(categoryId: string, propertyType: PropertyType): FieldValues {
  if (!categoryId) return {};
  const category = getCategoryById(categoryId, propertyType);
  if (!category) return {};

  const fieldValues: Record<string, CurrencyValue> = {};
  for (const field of category.fields) {
    fieldValues[field.id] = 0;
  }

  return fieldValues;
}

// Create empty category data with appropriate structure
function createEmptyCategoryData(
  categoryId: string,
  unitCount: number,
  propertyType: PropertyType
): CategoryData {
  const category = getCategoryById(categoryId, propertyType);
  if (!category) {
    return {
      buildingWide: {},
      units: [],
      description: '',
    };
  }

  const buildingWide = createEmptyFieldValues(categoryId, propertyType);

  // Create unit arrays only for per-unit categories
  const units: FieldValues[] = [];
  if (category.scope === 'per-unit') {
    const safeUnitCount = Number.isFinite(unitCount) ? Math.max(0, Math.trunc(unitCount)) : 0;
    for (let i = 0; i < safeUnitCount; i++) {
      units.push(createEmptyFieldValues(categoryId, propertyType));
    }
  }

  return {
    buildingWide,
    units,
    description: '',
  };
}

// Create initial contingency data
function createInitialContingencyData(subtotal: CurrencyValue = 0): ContingencyData {
  const calculatedValue = calculateContingency(subtotal);
  return {
    value: calculatedValue,
    mode: 'auto',
    calculatedValue,
  };
}

// Initialize a complete SOWData structure
export function initializeSOWData(
  propertyType: PropertyType = sowConfig.defaultPropertyType,
  unitCount: number = 1,
  propertyAddress: string = ''
): SOWData {
  // Enforce constraints
  const normalizedUnitCount = Number.isFinite(unitCount) ? Math.trunc(unitCount) : sowConfig.minUnits;
  const effectiveUnitCount = propertyType === PropertyType.SFR
    ? 1
    : Math.max(sowConfig.minUnits, Math.min(normalizedUnitCount, sowConfig.maxUnits));

  // Initialize categories using property-type-specific category list
  const categories: Record<string, CategoryData> = {};
  for (const category of getCategoriesForPropertyType(propertyType)) {
    categories[category.id] = createEmptyCategoryData(category.id, effectiveUnitCount, propertyType);
  }

  const subtotal = 0;
  const contingency = createInitialContingencyData(subtotal);

  return {
    propertyAddress,
    propertyType,
    unitCount: effectiveUnitCount,
    categories,
    contingency,
    subtotal,
    total: contingency.value,
  };
}

// Merge partial data with initialized defaults
export function mergeWithDefaults(partialData: Partial<SOWData>): SOWData {
  const safePartial: Partial<SOWData> =
    partialData && typeof partialData === 'object' ? partialData : {};

  const propertyType = safePartial.propertyType ?? sowConfig.defaultPropertyType;
  const unitCount = safePartial.unitCount ?? (propertyType === PropertyType.SFR ? 1 : sowConfig.minUnits);
  const propertyAddress = safePartial.propertyAddress ?? '';

  // Start with initialized data
  const baseData = initializeSOWData(propertyType, unitCount, propertyAddress);

  // Merge categories
  let categories: Record<string, CategoryData> = { ...baseData.categories };
  if (safePartial.categories && typeof safePartial.categories === 'object') {
    for (const [categoryId, categoryData] of Object.entries(safePartial.categories)) {
      const existing = categories[categoryId];
      if (!existing) continue;

      categories = {
        ...categories,
        [categoryId]: {
          ...existing,
          ...categoryData,
          buildingWide: {
            ...existing.buildingWide,
            ...categoryData.buildingWide,
          },
          // Iterate over existing.units to preserve all units, merging in partial data when available
          units: categoryData.units?.length
            ? existing.units.map((existingUnit, i) => ({
              ...existingUnit,
              ...(categoryData.units[i] ?? {}),
            }))
            : existing.units,
        },
      };
    }
  }

  // Merge contingency
  const contingency = safePartial.contingency
    ? {
      ...baseData.contingency,
      ...safePartial.contingency,
    }
    : baseData.contingency;

  // Recalculate totals
  return recalculateTotals({
    ...baseData,
    categories,
    contingency,
    propertyAddress,
    propertyType,
    unitCount,
  } as SOWData);
}

// Add a unit to the data structure
// Returns null if already at max units or if SFR
function addUnit(data: SOWData): SOWData | null {
  if (data.propertyType === PropertyType.SFR) return null;
  if (data.unitCount >= sowConfig.maxUnits) return null;

  const newUnitCount = data.unitCount + 1;
  const newCategories = { ...data.categories };

  // Add new unit to per-unit categories
  for (const category of sowConfig.categories) {
    if (category.scope === 'per-unit') {
      const categoryData = newCategories[category.id];
      if (!categoryData) continue;
      newCategories[category.id] = {
        ...categoryData,
        units: [...categoryData.units, createEmptyFieldValues(category.id, data.propertyType)],
      };
    }
  }

  return recalculateTotals({
    ...data,
    unitCount: newUnitCount,
    categories: newCategories,
  });
}

// Remove the last unit from the data structure
// Returns null if already at min units or if SFR
function removeUnit(data: SOWData): SOWData | null {
  if (data.propertyType === PropertyType.SFR) return null;
  if (data.unitCount <= sowConfig.minUnits) return null;

  const newUnitCount = data.unitCount - 1;
  const newCategories = { ...data.categories };

  // Remove last unit from per-unit categories
  for (const category of sowConfig.categories) {
    if (category.scope === 'per-unit') {
      const categoryData = newCategories[category.id];
      if (!categoryData) continue;
      newCategories[category.id] = {
        ...categoryData,
        units: categoryData.units.slice(0, -1),
      };
    }
  }

  return recalculateTotals({
    ...data,
    unitCount: newUnitCount,
    categories: newCategories,
  });
}

// Switch property type
// Preserves building-wide data; resets/adjusts unit data
export function switchPropertyType(data: SOWData, newType: PropertyType): SOWData {
  if (data.propertyType === newType) return data;

  const newUnitCount = newType === PropertyType.SFR ? 1 : sowConfig.minUnits;
  const newCategories: Record<string, CategoryData> = {};

  // Use property-type-specific categories to ensure correct schema for new type
  for (const category of getCategoriesForPropertyType(newType)) {
    const oldData = data.categories[category.id];

    if (category.scope === 'building-wide') {
      // Re-create building-wide data with correct fields for new property type
      // Preserve values for fields that exist in both schemas
      const newBuildingWide: Record<string, CurrencyValue> = { ...createEmptyFieldValues(category.id, newType) };
      if (oldData?.buildingWide) {
        for (const fieldId of Object.keys(newBuildingWide)) {
          if (fieldId in oldData.buildingWide) {
            newBuildingWide[fieldId] = oldData.buildingWide[fieldId];
          }
        }
      }
      newCategories[category.id] = {
        buildingWide: newBuildingWide,
        units: [],
        description: oldData?.description ?? '',
      };
    } else {
      // For per-unit categories, preserve building-wide values but reset units
      const newBuildingWide: Record<string, CurrencyValue> = { ...createEmptyFieldValues(category.id, newType) };
      if (oldData?.buildingWide) {
        for (const fieldId of Object.keys(newBuildingWide)) {
          if (fieldId in oldData.buildingWide) {
            newBuildingWide[fieldId] = oldData.buildingWide[fieldId];
          }
        }
      }

      const newUnits: FieldValues[] = [];
      for (let i = 0; i < newUnitCount; i++) {
        // Try to preserve existing unit data if available, with correct field schema
        const emptyUnit: Record<string, CurrencyValue> = { ...createEmptyFieldValues(category.id, newType) };
        if (oldData?.units[i]) {
          for (const fieldId of Object.keys(emptyUnit)) {
            if (fieldId in oldData.units[i]) {
              emptyUnit[fieldId] = oldData.units[i][fieldId];
            }
          }
        }
        newUnits.push(emptyUnit);
      }

      newCategories[category.id] = {
        buildingWide: newBuildingWide,
        units: newUnits,
        description: oldData?.description ?? '',
      };
    }
  }

  return recalculateTotals({
    ...data,
    propertyType: newType,
    unitCount: newUnitCount,
    categories: newCategories,
  });
}

// Update a single field value
export function updateFieldValue(
  data: SOWData,
  categoryId: string,
  fieldId: string,
  value: CurrencyValue,
  unitIndex?: number
): SOWData {
  if (!categoryId || !fieldId) return data;
  if (!Number.isFinite(value)) return data;
  const categoryData = data.categories[categoryId];
  if (!categoryData) return data;

  let newCategoryData: CategoryData;

  if (unitIndex === undefined || unitIndex === -1) {
    // Update building-wide value
    newCategoryData = {
      ...categoryData,
      buildingWide: {
        ...categoryData.buildingWide,
        [fieldId]: value,
      },
    };
  } else {
    // Bounds check for unit index
    if (unitIndex < 0 || unitIndex >= categoryData.units.length) {
      return data;
    }

    // Update unit value
    const newUnits = [...categoryData.units];
    newUnits[unitIndex] = {
      ...newUnits[unitIndex],
      [fieldId]: value,
    };
    newCategoryData = {
      ...categoryData,
      units: newUnits,
    };
  }

  return recalculateTotals({
    ...data,
    categories: {
      ...data.categories,
      [categoryId]: newCategoryData,
    },
  });
}

// Update a category description
export function updateDescription(
  data: SOWData,
  categoryId: string,
  description: string
): SOWData {
  if (!categoryId) return data;
  const categoryData = data.categories[categoryId];
  if (!categoryData) return data;

  const newCategoryData: CategoryData = {
    ...categoryData,
    description,
  };

  return {
    ...data,
    categories: {
      ...data.categories,
      [categoryId]: newCategoryData,
    },
  };
}

// Update contingency mode
export function updateContingencyMode(
  data: SOWData,
  mode: 'auto' | 'manual'
): SOWData {
  const calculatedValue = calculateContingency(data.subtotal);
  const newValue = mode === 'auto' ? calculatedValue : data.contingency.value;

  return {
    ...data,
    contingency: {
      value: newValue,
      mode,
      calculatedValue,
    },
    total: data.subtotal + newValue,
  };
}

// Update contingency value (only effective in manual mode)
export function updateContingencyValue(data: SOWData, value: CurrencyValue): SOWData {
  if (data.contingency.mode === 'auto') return data;
  if (!Number.isFinite(value)) return data;
  const clampedValue = Math.max(sowConfig.contingencyMinimum, Math.round(value));
  return {
    ...data,
    contingency: {
      ...data.contingency,
      value: clampedValue,
    },
    total: data.subtotal + clampedValue,
  };
}

// Clamp unit count to valid range based on property type
function clampUnitCount(propertyType: PropertyType, count: number): number {
  if (propertyType === PropertyType.SFR) return 1;
  const safe = Number.isFinite(count) ? Math.trunc(count) : sowConfig.minUnits;
  return Math.max(sowConfig.minUnits, Math.min(safe, sowConfig.maxUnits));
}

// Set unit count to a specific value, adding or removing units as needed
// Returns null if no change is needed
export function setUnitCount(data: SOWData, targetCount: number): SOWData | null {
  const clampedCount = clampUnitCount(data.propertyType, targetCount);
  if (clampedCount === data.unitCount) return null;

  let result: SOWData = data;

  if (clampedCount > data.unitCount) {
    // Add units
    for (let i = data.unitCount; i < clampedCount; i++) {
      const added = addUnit(result);
      if (added) result = added;
    }
  } else {
    // Remove units
    for (let i = data.unitCount; i > clampedCount; i--) {
      const removed = removeUnit(result);
      if (removed) result = removed;
    }
  }

  return result;
}
